import { Box, Button, Typography } from '@mui/material';
import { Component, ErrorInfo, ReactNode } from 'react';
import { isStaleAssetError, recoverFromStaleAssets } from '../../common/staleAssetRecovery';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // React.lazy() chunk-load failures (e.g. after a deploy invalidates an
    // old hashed filename) surface here rather than as a window-level
    // 'error'/'unhandledrejection' event, since React catches them
    // internally to hand off to the boundary. Attempt the same one-shot
    // cache-clear-and-reload used for those global cases instead of
    // leaving the user stuck on this fallback screen.
    if (isStaleAssetError(error)) {
      void recoverFromStaleAssets();
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            textAlign: 'center',
            p: 3,
            bgcolor: 'background.default',
            color: 'text.primary',
          }}
        >
          <Typography variant="h4" gutterBottom>
            Oops, something went wrong.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
            An unexpected error has occurred. We have logged the issue, but you may need to reload the page to continue.
          </Typography>
          <Button variant="contained" color="primary" onClick={this.handleReload}>
            Reload Page
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
